import { useCallback, useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { Spinner } from "@/components/ui";
import { platformAccount, type PlatformAccount } from "@/auth/api";
import type { PlatformAdmin } from "@/types/api";
import { PlatformChangePassword } from "./PlatformChangePassword";

// Gates the /platform area against the SEPARATE platform session (authkit_padmin
// cookie), independent of the tenant AuthProvider. A guest is sent to the
// separate /admin/login page. A boot-seeded admin who still owes the forced
// first-login password change (6.6) is held on the change screen until it clears.
// Otherwise the admin is passed down via Outlet context.
export function PlatformProtectedRoute() {
  const [account, setAccount] = useState<PlatformAccount | null>(null);
  const [status, setStatus] = useState<"loading" | "authed" | "guest">(
    "loading",
  );

  const load = useCallback((signal?: AbortSignal) => {
    return platformAccount(signal)
      .then((a) => {
        if (signal?.aborted) return; // stale call (StrictMode remount / unmount)
        setAccount(a);
        setStatus(a ? "authed" : "guest");
      })
      .catch(() => {
        // An aborted fetch (effect cleanup) is NOT a sign-in failure; ignore it,
        // or we'd flip to "guest" and bounce a freshly-authenticated admin back to
        // /login. Only a real response (handled above) decides authed/guest.
        if (signal?.aborted) return;
        setStatus("guest");
      });
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  if (status === "loading") {
    return (
      <div className="grid h-dvh place-items-center bg-bg text-muted">
        <Spinner size={28} />
      </div>
    );
  }
  if (status === "guest" || !account) {
    return <Navigate to="/admin/login" replace />;
  }
  // Forced first-login password change: block the console until the seed password
  // is replaced. On success, re-check (the flag is now clear → the console opens).
  if (account.password_change_required) {
    return <PlatformChangePassword onDone={() => void load()} />;
  }
  const admin: PlatformAdmin = {
    email: account.email,
    name: account.name,
    role: account.role,
    // Carried down so the console can gate what it draws (platform/access). If it
    // ever arrives empty the console shows only the dashboard - `can` fails closed,
    // so a missing list hides screens rather than offering ones the API refuses.
    capabilities: account.capabilities,
  };
  return <Outlet context={admin satisfies PlatformAdmin | null} />;
}
